import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { motion } from "framer-motion";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [mode, setMode] = useState(sp.get("mode") === "link" ? "link" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (mode === "link") {
      try { await api.post("/auth/request-password-link", { email: form.email, origin_url: window.location.origin }); toast.success("If that email has a booking with us, a 'Set your password' link is on its way"); setMode("login"); } finally { setLoading(false); }
      return;
    }
    try {
      let user;
      if (mode === "login") {
        user = await login(form.email, form.password);
      } else {
        user = await register({ email: form.email, password: form.password, name: form.name, role: "client" });
      }
      toast.success(`Welcome, ${user.name}!`);
      const staff = ["admin", "manager", "producer", "editor"];
      const next = sp.get("next");
      const isStaff = staff.includes(user.role);
      navigate(next && next.startsWith(isStaff ? "/admin" : "/client") ? next : isStaff ? "/admin" : "/client");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-b from-[#13041C] to-[#2A0A42] p-12 relative overflow-hidden">
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-brand-purple/40 blur-3xl" />
        <div className="absolute top-10 right-0 w-72 h-72 rounded-full bg-brand-orange/20 blur-3xl" />
        <Logo dark />
        <div className="relative">
          <h2 className="font-heading font-extrabold text-4xl text-white leading-tight">
            Your project.<br />One easy place.
          </h2>
          <p className="mt-4 text-white/60 max-w-sm">
            Track progress, watch your draft video, leave comments and approve — all from your EasyVideo login.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-white/70">
            <span className="w-2.5 h-2.5 rounded-full bg-brand-orange" /> Planning
            <span className="w-6 h-px bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-brand-orange" /> Filming
            <span className="w-6 h-px bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/30" /> Review
          </div>
        </div>
        <span className="relative text-white/40 text-sm">Easy video. Great results.</span>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-heading font-extrabold text-3xl text-brand-purpleDeep">
            {mode === "login" ? "Welcome back" : mode === "link" ? "Set or reset your password" : "Create your account"}
          </h1>
          <p className="mt-2 text-brand-purpleDeep/60">
            {mode === "login" ? "Log in to your EasyVideo portal." : "Track your video project with us."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-sm font-medium text-brand-purpleDeep/70 mb-1.5">Name</label>
                <input className={inp} value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="login-name" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-brand-purpleDeep/70 mb-1.5">Email</label>
              <input type="email" className={inp} value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="login-email" />
            </div>
            {mode !== "link" && <div>
              <label className="block text-sm font-medium text-brand-purpleDeep/70 mb-1.5">Password</label>
              <input type="password" className={inp} value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="login-password" />
            </div>}
            {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeDark disabled:opacity-50 text-white font-semibold py-3.5 rounded-lg transition-colors" data-testid="login-submit">
              {loading ? "Please wait…" : mode === "login" ? "Log in" : mode === "link" ? "Email me a link" : "Create account"} <ArrowRight size={17} />
            </button>
          </form>

          <div className="mt-6 text-sm text-brand-purpleDeep/60">
            {mode === "login" ? (
              <>New here? <button onClick={() => { setMode("register"); setError(""); }} className="text-brand-purple font-semibold" data-testid="toggle-register">Create an account</button></>
            ) : (
              <>Already have an account? <button onClick={() => { setMode("login"); setError(""); }} className="text-brand-purple font-semibold" data-testid="toggle-login">Log in</button></>
            )}
          </div>
          {mode === "login" && <button onClick={() => { setMode("link"); setError(""); }} className="mt-3 block text-sm text-brand-purple" data-testid="toggle-password-link">Booked without a password, or forgot it? Get a set-password link</button>}
          <Link to="/" className="mt-4 inline-block text-sm text-brand-purpleDeep/40 hover:text-brand-purple">← Back to website</Link>
        </motion.div>
      </div>
    </div>
  );
}

const inp = "w-full bg-[#F3F0F5] border-none rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-brand-purple/20 focus:bg-white outline-none transition-all";
